import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SettingsShell from './SettingsShell';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';

const NotificationsSettings = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    reminderEnabled: user?.reminderEnabled ?? true,
    reminderLeadMinutes: user?.reminderLeadMinutes ?? 5,
    dailySummaryEnabled: user?.dailySummaryEnabled ?? true,
    dailySummaryTime: user?.dailySummaryTime || '08:00',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData({
      reminderEnabled: user?.reminderEnabled ?? true,
      reminderLeadMinutes: user?.reminderLeadMinutes ?? 5,
      dailySummaryEnabled: user?.dailySummaryEnabled ?? true,
      dailySummaryTime: user?.dailySummaryTime || '08:00',
    });
  }, [
    user?.reminderEnabled,
    user?.reminderLeadMinutes,
    user?.dailySummaryEnabled,
    user?.dailySummaryTime,
  ]);

  const baseline = {
    reminderEnabled: user?.reminderEnabled ?? true,
    reminderLeadMinutes: user?.reminderLeadMinutes ?? 5,
    dailySummaryEnabled: user?.dailySummaryEnabled ?? true,
    dailySummaryTime: user?.dailySummaryTime || '08:00',
  };

  const isDirty =
    formData.reminderEnabled !== baseline.reminderEnabled ||
    formData.reminderLeadMinutes !== baseline.reminderLeadMinutes ||
    formData.dailySummaryEnabled !== baseline.dailySummaryEnabled ||
    formData.dailySummaryTime !== baseline.dailySummaryTime;

  useUnsavedChanges(isDirty);

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const rawTime = formData.dailySummaryTime || '08:00';
      const [h, m] = rawTime.trim().split(':');
      const dailySummaryTime = `${String(Number(h) || 0).padStart(2, '0')}:${String(Number(m) || 0).padStart(2, '0')}`;

      await updateUser({
        reminderEnabled: formData.reminderEnabled,
        reminderLeadMinutes: formData.reminderLeadMinutes,
        dailySummaryEnabled: formData.dailySummaryEnabled,
        dailySummaryTime,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to update notifications';
      setError(typeof msg === 'string' ? msg : 'Failed to update notifications');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsShell
      title="Notifications"
      description="Control reminder emails and daily summaries."
    >
      {success && (
        <div role="status" className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Saved
        </div>
      )}
      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm space-y-6">
        <label htmlFor="email-reminders-enabled" className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer focus-within:ring-2 focus-within:ring-primary-500">
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Email reminders</div>
            <div className="text-xs text-gray-500">Alerts before follow-ups are due.</div>
          </div>
          <input
            id="email-reminders-enabled"
            type="checkbox"
            role="switch"
            checked={formData.reminderEnabled}
            onChange={(event) => setFormData({ ...formData, reminderEnabled: event.target.checked })}
            className="sr-only"
          />
          <span aria-hidden="true" className={`w-10 h-5 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${formData.reminderEnabled ? 'bg-primary-600' : 'bg-gray-300'}`}>
            <div className={`bg-white w-3 h-3 rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${formData.reminderEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </span>
        </label>

        {formData.reminderEnabled && (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
            <label className="block text-sm font-medium text-gray-700" htmlFor="reminder-lead-minutes">
              Lead Time (Minutes)
            </label>
            <input
              id="reminder-lead-minutes"
              type="number"
              min={0}
              max={1440}
              value={formData.reminderLeadMinutes}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                const safeValue = Number.isFinite(nextValue)
                  ? Math.max(0, Math.min(1440, Math.round(nextValue)))
                  : 0;
                setFormData({
                  ...formData,
                  reminderLeadMinutes: safeValue,
                });
              }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-gray-900 font-medium transition-all"
            />
          </div>
        )}

        <div className="pt-2 border-t border-gray-100" />

        <label htmlFor="daily-summary-enabled" className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer focus-within:ring-2 focus-within:ring-primary-500">
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Daily summary</div>
            <div className="text-xs text-gray-500">Morning recap of active leads.</div>
          </div>
          <input
            id="daily-summary-enabled"
            type="checkbox"
            role="switch"
            checked={formData.dailySummaryEnabled}
            onChange={(event) => setFormData({ ...formData, dailySummaryEnabled: event.target.checked })}
            className="sr-only"
          />
          <span aria-hidden="true" className={`w-10 h-5 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${formData.dailySummaryEnabled ? 'bg-primary-600' : 'bg-gray-300'}`}>
            <div className={`bg-white w-3 h-3 rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${formData.dailySummaryEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </span>
        </label>

        {formData.dailySummaryEnabled && (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
            <label className="block text-sm font-medium text-gray-700" htmlFor="daily-summary-time">
              Delivery Time
            </label>
            <div className="relative group">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              <input
                id="daily-summary-time"
                type="time"
                value={formData.dailySummaryTime}
                onChange={(event) => setFormData({
                  ...formData,
                  dailySummaryTime: event.target.value,
                })}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white text-gray-900 font-medium transition-all"
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !isDirty}
          className="w-full bg-primary-600 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-700 active:bg-primary-800 transition-all disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Notifications'
          )}
        </button>
      </form>
    </SettingsShell>
  );
};

export default NotificationsSettings;
